"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { IoArrowBackOutline } from "react-icons/io5";
import { getUser, setSession, setUser } from "~/lib/session";
import { getRequest, getRequestWithAuth, postRequest } from "~/lib/api";
import { useAuth } from "~/contexts/AuthContext";
import { FcGoogle } from "react-icons/fc";

export default function LoginForm() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const showGoogleAuth = import.meta.env.VITE_GOOGLE_AUTH_ENABLED === "true";

  useEffect(() => {
    const user = getUser();
    if (user) {
      navigate("/session", { replace: true });
    }
  }, [navigate]);

  const signInWithGoogle = async () => {
    const data = await getRequest("/api/v1/auth/google/authorize");
    window.location.href = data?.authorization_url;
  };

  const signInWithMicrosoft = async () => {
    const data = await getRequest("/api/v1/auth/microsoft/authorize");
    window.location.href = data?.authorization_url;
  };

  const handleLoginError = (error: any) => {
    if (
      error.response?.data?.detail &&
      error.response?.data?.detail.includes("LOGIN_BAD_CREDENTIALS")
    ) {
      setError("Invalid username or password");
    } else {
      setError("Login failed. Please try again.");
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Create URL-encoded form data for the login request
      const formData = new URLSearchParams();
      formData.append("username", username);
      formData.append("password", password);

      const response = await postRequest("/api/v1/auth/jwt/login", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      // Store the session
      setSession(response.access_token, response.token_type);

      // Get user details and store them
      const user = await getRequestWithAuth("/api/v1/users/me");
      setUser(user);

      // Refresh auth context with new user
      await refreshAuth();

      // Redirect to dashboard
      navigate("/session");
    } catch (error: any) {
      handleLoginError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100">
      <main className="w-full max-w-md p-8">
        {/* Login Form */}
        <fieldset className="fieldset w-full">
          <legend className="fieldset-legend text-2xl font-semibold mb-6 text-center">
            Login to your account
          </legend>

          <form onSubmit={handleLogin} className="space-y-6">
            {/* Username Field */}
            <label className="form-control w-full space-y-2">
              <div className="label">
                <span className="label-text">Username</span>
              </div>
              <input
                type="text"
                placeholder="Enter your username"
                className="input input-bordered w-full mb-8 focus:outline-none"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
              />
            </label>

            {/* Password Field */}
            <label className="form-control w-full space-y-2">
              <div className="label">
                <span className="label-text">Password</span>
              </div>
              <input
                type="password"
                placeholder="Enter your password"
                className="input input-bordered w-full mb-8 focus:outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </label>

            {/* Error Display */}
            {error && (
              <div className="alert alert-error">
                <span>{error}</span>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading || !username || !password}
              className="btn btn-soft w-full"
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>

          {/* Google OAuth Section */}
          {showGoogleAuth && (
            <>
              <div className="divider my-6">OR</div>
              <div className="flex flex-col gap-6">
              <button
                onClick={signInWithGoogle}
                className="btn bg-white text-black border-base-300"
              >
                <FcGoogle size={18} />
                Login with Google
              </button>
              <button
                onClick={signInWithMicrosoft}
                className="btn bg-[#2F2F2F] text-white border-black"
              >
                <svg
                  aria-label="Microsoft logo"
                  width="18"
                  height="18"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path d="M96 96H247V247H96" fill="#f24f23"></path>
                  <path d="M265 96V247H416V96" fill="#7eba03"></path>
                  <path d="M96 265H247V416H96" fill="#3ca4ef"></path>
                  <path d="M265 265H416V416H265" fill="#f9ba00"></path>
                </svg>
                Login with Microsoft
              </button>
              </div>
            </>
          )}

          {/* Back Button */}
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost w-full mt-4"
            aria-label="Go back to homepage"
          >
            <IoArrowBackOutline size={18} />
          </button>
        </fieldset>
      </main>
    </div>
  );
}
